function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiAlert from '@material-ui/core/Alert';
import MuiAlertTitle from '@material-ui/core/AlertTitle';
import { AnimatePresence, AnimateSharedLayout, motion } from 'framer-motion';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import DropZone from '../DropZone';
import FormControl from '../FormControl';
import Button from '../Button';
import Confirmation from '../Confirmation';
import IconButton from '../IconButton';
import FileViewer from '../FileViewer';
import FormFocus from '../FormFocus';
import Grid from '../Grid';
import Tooltip from '../Tooltip';
import { getFileName } from '../../utils/helpers';
var _borderRadius = function borderRadius(edges, theme) {
  switch (edges) {
    case 'rounded':
      return 99;
    case 'sharp':
      return 0;
    default:
      return theme.shape.borderRadius;
  }
};
var useStyles = makeStyles(function (theme) {
  return {
    saved: {
      borderRadius: function borderRadius(_ref) {
        var edges = _ref.edges;
        return _borderRadius(edges, theme);
      },
      border: "1px solid " + theme.palette.text.secondary,
      padding: theme.spacing(6, 4),
      '& .MuiAlert-message': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }
    },
    loading: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -20,
      marginLeft: -20,
      zIndex: 1
    },
    fileviewer: {
      '& .MuiPaper-root.MuiDialog-paper': {
        minHeight: '80vh'
      }
    },
    title: {
      fontSize: 14,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      marginBottom: 0
    }
  };
});
var FileUpload = function FileUpload(_ref2) {
  var fileActions = _ref2.fileActions,
    fileErrors = _ref2.fileErrors,
    disabled = _ref2.disabled,
    dropzoneLabel = _ref2.dropzoneLabel,
    dropzoneDescription = _ref2.dropzoneDescription,
    helperText = _ref2.helperText,
    helperTextPlacement = _ref2.helperTextPlacement,
    label = _ref2.label,
    required = _ref2.required,
    showError = _ref2.showError,
    errorMessage = _ref2.errorMessage,
    edges = _ref2.edges,
    dropzoneProps = _ref2.dropzoneProps,
    onChange = _ref2.onChange,
    onDelete = _ref2.onDelete,
    saving = _ref2.saving,
    name = _ref2.name,
    files = _ref2.files;
  var _useState = useState(false),
    cancelling = _useState[0],
    setCancelling = _useState[1];
  var _useState2 = useState(null),
    confirmDelete = _useState2[0],
    setConfirmDelete = _useState2[1];
  var _useState3 = useState(false),
    confirmDeleteAll = _useState3[0],
    setConfirmDeleteAll = _useState3[1];
  var _useState4 = useState(null),
    viewedFile = _useState4[0],
    setViewedFile = _useState4[1];
  var _useState5 = useState(false),
    loading = _useState5[0],
    setLoading = _useState5[1];
  var classes = useStyles({
    edges: edges,
    disabled: disabled,
    showError: showError
  });
  var theme = useTheme();
  var isNotMobile = useMediaQuery(theme.breakpoints.up('md'));
  var handleFileSave = useCallback(function (results) {
    var uploadedFiles = Array.isArray(files) ? [].concat(files) : [];
    var promises = [];
    results.forEach(function (file) {
      return promises.push(new Promise(function (resolve) {
        var reader = new FileReader();
        reader.onload = function () {
          uploadedFiles.push({
            url: reader.result,
            filename: file.path,
            filetype: file.type
          });
          resolve();
        };
        reader.readAsDataURL(file);
      }));
    });
    Promise.all(promises).then(function () {
      setLoading(false);
      onChange(uploadedFiles);
    });
  }, [files, onChange]);
  var onDrop = function onDrop(results) {
    setLoading(true);
    setTimeout(function () {
      return handleFileSave(results);
    }, 500);
  };
  var handleDelete = function handleDelete() {
    var updatedFiles = [].concat(files);
    updatedFiles.splice(confirmDelete, 1);
    onDelete(updatedFiles, files[confirmDelete], confirmDelete).then(function () {
      onChange(updatedFiles);
      setLoading(false);
      setConfirmDelete(null);
    })
    // eslint-disable-next-line no-console
    .catch(function (e) {
      return console.error(e);
    });
  };
  var handleDeleteWithoutConfirmation = function handleDeleteWithoutConfirmation(index) {
    var updatedFiles = [].concat(files);
    updatedFiles.splice(index, 1);
    onDelete(updatedFiles, files[index]).then(function () {
      onChange(updatedFiles);
      setLoading(false);
    })
    // eslint-disable-next-line no-console
    .catch(function (e) {
      return console.error(e);
    });
  };
  var handleDeleteConfirmation = function handleDeleteConfirmation(index) {
    setLoading(true);
    setConfirmDelete(index);
  };
  var handleDeleteAll = function handleDeleteAll() {
    setCancelling(true);
    setConfirmDeleteAll(false);
    onDelete().then(function () {
      onChange([]);
      setCancelling(false);
    });
  };
  var fade = {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormControl, {
    name: name,
    fullWidth: true,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges
  }, /*#__PURE__*/React.createElement(FormFocus, {
    name: name
  }), files && files.length > 0 && /*#__PURE__*/React.createElement(motion.div, _extends({}, fade, {
    className: classes.saved
  }), (saving || loading) && /*#__PURE__*/React.createElement(MuiCircularProgress, {
    className: classes.loading,
    disableShrink: true
  }), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignItems: "center",
    spacing: 5
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(AnimateSharedLayout, null, files.map(function (file, index) {
    return /*#__PURE__*/React.createElement(AnimatePresence, {
      key: getFileName(file)
    }, /*#__PURE__*/React.createElement(motion.div, {
      positionTransition: true,
      initial: {
        opacity: 0,
        y: 50,
        scale: 0.3
      },
      animate: {
        opacity: 1,
        y: 0,
        scale: 1
      },
      exit: {
        opacity: 0,
        scale: 0.5,
        transition: {
          duration: 0.2
        }
      }
    }, /*#__PURE__*/React.createElement(MuiAlert, {
      severity: showError || fileErrors(file, index) !== null ? 'error' : 'success',
      style: {
        marginBottom: 5
      },
      action: /*#__PURE__*/React.createElement(React.Fragment, null, fileActions(file, index) !== null && fileActions(file, index), /*#__PURE__*/React.createElement(Button, {
        onClick: function onClick() {
          return setViewedFile(file);
        },
        color: "inherit",
        size: "small",
        variant: "text"
      }, "View"), /*#__PURE__*/React.createElement(Tooltip, {
        title: "Delete",
        enterDelay: 0
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(IconButton, {
        disabled: saving,
        size: "small",
        "aria-label": "delete",
        style: {
          padding: 0
        },
        onClick: function onClick() {
          return !showError ? handleDeleteConfirmation(index) : handleDeleteWithoutConfirmation(index);
        }
      }, /*#__PURE__*/React.createElement(DeleteIcon, null)))))
    }, /*#__PURE__*/React.createElement(MuiAlertTitle, {
      className: classes.title
    }, getFileName(file)), fileErrors(file, index))));
  }))))), (!files || files.length === 0 || !dropzoneProps || dropzoneProps.maxFiles === undefined || dropzoneProps.maxFiles > 1) && /*#__PURE__*/React.createElement(motion.div, _extends({}, fade, {
    style: files && files.length > 0 ? {
      marginTop: 8
    } : null
  }), /*#__PURE__*/React.createElement(DropZone, {
    loading: loading,
    disabled: disabled,
    onDrop: onDrop,
    edges: edges,
    dropzoneLabel: dropzoneLabel,
    dropzoneDescription: dropzoneDescription,
    dropzoneProps: dropzoneProps,
    compact: files && files.length > 0
  }))), /*#__PURE__*/React.createElement(Confirmation, {
    open: confirmDeleteAll,
    onCancel: function onCancel() {
      return setConfirmDeleteAll(false);
    },
    onConfirm: handleDeleteAll,
    message: "Are you sure you want to remove the attached file(s)?"
  }), /*#__PURE__*/React.createElement(Confirmation, {
    open: confirmDelete !== null,
    onCancel: function onCancel() {
      setConfirmDelete(null);
      setLoading(false);
    },
    onConfirm: handleDelete,
    message: "Are you sure you want to remove the attached file?"
  }), /*#__PURE__*/React.createElement(FileViewer, {
    file: viewedFile,
    open: viewedFile !== null,
    onClose: function onClose() {
      return setViewedFile(null);
    }
  }));
};
FileUpload.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Add actions to file line items
   */
  fileActions: PropTypes.func,
  /**
   * Add error messages to file line items
   */
  fileErrors: PropTypes.func,
  /**
   * Array of existing images already uploaded to this field
   */
  files: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    filename: PropTypes.string,
    filetype: PropTypes.string
  })),
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Text label displayed in the dropzone
   */
  dropzoneLabel: PropTypes.string,
  /**
   * Text description displayed in the dropzone
   */
  dropzoneDescription: PropTypes.string,
  /**
   * Pass props for react-dropzone. See https://react-dropzone.js.org/
   */
  dropzoneProps: PropTypes.object,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The id of the `input` element.
   * Use this prop to make `label` and `helperText` accessible for screen readers.
   */
  htmlFor: PropTypes.string,
  /**
   * Do something when user saves cropped image, received the base64 string
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Show uploading state
   */
  saving: PropTypes.bool,
  /**
   * Do something when user removes image
   */
  onDelete: PropTypes.func,
  /**
   * Pass through props for hidden input that holds the data url of the cropped image
   */
  inputProps: PropTypes.object,
  /**
   * The field name.
   */
  name: PropTypes.string.isRequired,
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * If `true`, the label will indicate that the input is required.
   */
  required: PropTypes.bool,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp'])
} : {};
FileUpload.defaultProps = {
  fileActions: function fileActions() {
    return null;
  },
  fileErrors: function fileErrors() {
    return null;
  },
  disabled: false,
  dropzoneLabel: 'Upload file(s)',
  dropzoneDescription: 'Drag and drop files here, or click to select files',
  helperText: '',
  saving: false,
  onDelete: function onDelete() {
    return Promise.resolve(true);
  },
  helperTextPlacement: 'above',
  htmlFor: '',
  label: '',
  required: false,
  showError: false,
  errorMessage: '',
  edges: 'normal',
  inputProps: null,
  dropzoneProps: null,
  files: null
};
export default FileUpload;